import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { moviesAPI, branchesAPI, showtimesAPI, campaignsAPI, contactMessagesAPI, analyticsAPI } from '../api';
import { 
  Film, 
  MapPin, 
  Calendar, 
  Tag, 
  TrendingUp, 
  Clock, 
  Users,
  Mail,
  Eye,
  Zap,
  BarChart3,
  Activity,
  Smartphone,
  Monitor
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    moviesNowPlaying: 0,
    moviesUpcoming: 0,
    moviesArchived: 0,
    branches: 0,
    showtimesThisWeek: 0,
    showtimesTotal: 0,
    campaigns: 0,
    unreadMessages: 0,
  });

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekRange, setWeekRange] = useState({ start: '', end: '' });

  useEffect(() => {
    loadStats();
    loadAnalytics();
  }, []);

  const getWeekRange = () => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 5 = Friday
    
    // Calculate days to Friday (cinema week starts on Friday)
    let daysToFriday = currentDay >= 5 ? currentDay - 5 : currentDay + 2;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - daysToFriday);
    weekStart.setHours(0, 0, 0, 0);
    
    // Week ends on Thursday
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    
    return {
      start: weekStart.toISOString().split('T')[0],
      end: weekEnd.toISOString().split('T')[0],
      startFormatted: weekStart.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
      endFormatted: weekEnd.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
    };
  };

  const loadStats = async () => {
    try {
      const week = getWeekRange();
      setWeekRange(week);
      
      const [moviesRes, branchesRes, showtimesRes, campaignsRes, messagesRes] = await Promise.all([
        moviesAPI.getAll(),
        branchesAPI.getAll(),
        showtimesAPI.getAll(),
        campaignsAPI.getAll(),
        contactMessagesAPI.getAll().catch(() => ({ data: [] })),
      ]);

      const movies = moviesRes.data;
      const showtimes = showtimesRes.data;
      
      // Filter showtimes for this week (Friday to Thursday)
      const showtimesThisWeek = showtimes.filter(showtime => {
        const showDate = showtime.show_date;
        return showDate >= week.start && showDate <= week.end;
      });

      // Categorize movies
      const nowPlaying = movies.filter(m => m.status === 'now_playing').length;
      const upcoming = movies.filter(m => m.status === 'upcoming').length;
      const archived = movies.filter(m => m.status === 'archived').length;

      // Unread messages
      const unread = messagesRes.data.filter(m => !m.is_read).length;

      setStats({
        moviesNowPlaying: nowPlaying,
        moviesUpcoming: upcoming,
        moviesArchived: archived,
        branches: branchesRes.data.length,
        showtimesThisWeek: showtimesThisWeek.length,
        showtimesTotal: showtimes.length,
        campaigns: campaignsRes.data.length,
        unreadMessages: unread,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await analyticsAPI.getDashboard();
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Analytics yükleme hatası:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-white text-xl">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-gray-400">Hoş geldiniz! İşte sisteminizin genel görünümü</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400 bg-gray-900 px-4 py-2 rounded-lg border border-gray-800">
            <Activity size={16} />
            <span>Son güncelleme: {new Date().toLocaleTimeString('tr-TR')}</span>
          </div>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Movies Card */}
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 p-6 hover:border-blue-500/40 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue-500/20 p-3 rounded-lg">
                <Film size={24} className="text-blue-400" />
              </div>
              <Link to="/admin/movies" className="text-xs text-blue-400 hover:text-blue-300">
                Detay →
              </Link>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Toplam Film</p>
              <p className="text-3xl font-bold text-white mb-3">
                {stats.moviesNowPlaying + stats.moviesUpcoming + stats.moviesArchived}
              </p>
              <div className="flex gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-400">Vizyonda: <span className="text-white font-semibold">{stats.moviesNowPlaying}</span></span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-400">Yakında: <span className="text-white font-semibold">{stats.moviesUpcoming}</span></span>
                </div>
              </div>
              <div className="flex gap-3 text-xs mt-1">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                  <span className="text-gray-400">Arşiv: <span className="text-white font-semibold">{stats.moviesArchived}</span></span>
                </div>
              </div>
            </div>
          </Card>

          {/* Showtimes Card */}
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20 p-6 hover:border-purple-500/40 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-purple-500/20 p-3 rounded-lg">
                <Calendar size={24} className="text-purple-400" />
              </div>
              <Link to="/admin/showtimes" className="text-xs text-purple-400 hover:text-purple-300">
                Detay →
              </Link>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Bu Hafta Seanslar</p>
              <p className="text-3xl font-bold text-white mb-3">{stats.showtimesThisWeek}</p>
              <div className="bg-purple-500/10 rounded px-2 py-1 text-xs text-purple-300">
                <Clock size={12} className="inline mr-1" />
                {weekRange.startFormatted} - {weekRange.endFormatted} (Cuma-Perşembe)
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Toplam: {stats.showtimesTotal} seans
              </div>
            </div>
          </Card>

          {/* Branches Card */}
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20 p-6 hover:border-green-500/40 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-green-500/20 p-3 rounded-lg">
                <MapPin size={24} className="text-green-400" />
              </div>
              <Link to="/admin/branches" className="text-xs text-green-400 hover:text-green-300">
                Detay →
              </Link>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Salonlar</p>
              <p className="text-3xl font-bold text-white mb-3">{stats.branches}</p>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <TrendingUp size={14} className="text-green-400" />
                <span>Aktif lokasyonlar</span>
              </div>
            </div>
          </Card>

          {/* Campaigns & Messages Card */}
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20 p-6 hover:border-orange-500/40 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-orange-500/20 p-3 rounded-lg">
                <Tag size={24} className="text-orange-400" />
              </div>
              <Link to="/admin/campaigns" className="text-xs text-orange-400 hover:text-orange-300">
                Detay →
              </Link>
            </div>
            <div>
              <p className="text-gray-400 text-sm mb-1">Kampanyalar</p>
              <p className="text-3xl font-bold text-white mb-3">{stats.campaigns}</p>
              <Link to="/admin/contact-messages" className="flex items-center gap-2 text-xs">
                <Mail size={14} className="text-orange-400" />
                <span className="text-gray-400">
                  {stats.unreadMessages > 0 ? (
                    <span className="text-orange-400 font-semibold">{stats.unreadMessages} yeni mesaj</span>
                  ) : (
                    <span>Mesaj yok</span>
                  )}
                </span>
              </Link>
            </div>
          </Card>
        </div>

        {/* Analytics Overview */}
        {analyticsData && (
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <BarChart3 size={24} className="text-yellow-500" />
                📊 Site Analitik Özeti
              </h2>
              <Link
                to="/admin/analytics"
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-all"
              >
                Detaylı Analitik →
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye size={18} className="text-blue-400" />
                  <p className="text-gray-400 text-sm">Sayfa Görüntüleme</p>
                </div>
                <p className="text-2xl font-bold text-white">
                  {analyticsData.summary.total_pageviews.toLocaleString()}
                </p>
              </div>

              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Activity size={18} className="text-purple-400" />
                  <p className="text-gray-400 text-sm">Toplam Event</p>
                </div>
                <p className="text-2xl font-bold text-white">
                  {analyticsData.summary.total_events.toLocaleString()}
                </p>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={18} className="text-green-400" />
                  <p className="text-gray-400 text-sm">Canlı Ziyaretçi</p>
                </div>
                <p className="text-2xl font-bold text-white">
                  {analyticsData.summary.live_visitors}
                </p>
                <p className="text-xs text-gray-500 mt-1">Son 5 dakika</p>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={18} className="text-orange-400" />
                  <p className="text-gray-400 text-sm">Admin İşlem</p>
                </div>
                <p className="text-2xl font-bold text-white">
                  {analyticsData.summary.total_admin_actions}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Device Distribution */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Monitor size={16} className="text-blue-400" />
                  Cihaz Dağılımı
                </h3>
                <div className="space-y-2">
                  {analyticsData.device_distribution.map((device, idx) => {
                    const deviceNames = { mobile: 'Mobil', desktop: 'Masaüstü', tablet: 'Tablet' };
                    const deviceIcons = { mobile: Smartphone, desktop: Monitor, tablet: Monitor };
                    const Icon = deviceIcons[device._id] || Monitor;
                    const total = analyticsData.device_distribution.reduce((sum, d) => sum + d.count, 0);
                    const percentage = ((device.count / total) * 100).toFixed(0);
                    
                    return (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="text-gray-400" />
                          <span className="text-gray-300">{deviceNames[device._id] || device._id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-yellow-500 font-semibold">{device.count}</span>
                          <span className="text-gray-500 text-xs">({percentage}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Popular Movies */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Film size={16} className="text-purple-400" />
                  Popüler Filmler
                </h3>
                <div className="space-y-2">
                  {analyticsData.popular_movies.slice(0, 5).map((movie, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300 truncate flex-1">{movie.title?.substring(0, 20)}...</span>
                      <span className="text-yellow-500 font-semibold">{movie.views}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Pages */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Eye size={16} className="text-green-400" />
                  Popüler Sayfalar
                </h3>
                <div className="space-y-2">
                  {analyticsData.popular_pages.slice(0, 5).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300 truncate flex-1 font-mono text-xs">{page._id}</span>
                      <span className="text-yellow-500 font-semibold">{page.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="bg-gray-900 border-gray-800 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Zap size={20} className="text-yellow-400" />
                Hızlı İşlemler
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/admin/movies/new"
                className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-lg hover:border-blue-500/40 transition-all group"
              >
                <Film size={20} className="text-blue-400 mb-2" />
                <p className="text-white font-semibold mb-1">Yeni Film Ekle</p>
                <p className="text-gray-400 text-sm">Film ekle ve yönet</p>
              </Link>
              
              <Link
                to="/admin/showtimes"
                className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-lg hover:border-purple-500/40 transition-all group"
              >
                <Calendar size={20} className="text-purple-400 mb-2" />
                <p className="text-white font-semibold mb-1">Seans Oluştur</p>
                <p className="text-gray-400 text-sm">Toplu seans ekle</p>
              </Link>
              
              <Link
                to="/admin/branches"
                className="p-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-lg hover:border-green-500/40 transition-all group"
              >
                <MapPin size={20} className="text-green-400 mb-2" />
                <p className="text-white font-semibold mb-1">Salon Yönetimi</p>
                <p className="text-gray-400 text-sm">Salonları düzenle</p>
              </Link>
              
              <Link
                to="/admin/campaigns"
                className="p-4 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-lg hover:border-orange-500/40 transition-all group"
              >
                <Tag size={20} className="text-orange-400 mb-2" />
                <p className="text-white font-semibold mb-1">Kampanya Ekle</p>
                <p className="text-gray-400 text-sm">Yeni kampanya oluştur</p>
              </Link>
            </div>
          </Card>

          {/* System Overview */}
          <Card className="bg-gray-900 border-gray-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 size={20} className="text-blue-400" />
                Sistem Özeti
              </h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-300 text-sm">Toplam Kayıt</span>
                </div>
                <span className="font-bold text-white">
                  {stats.moviesNowPlaying + stats.moviesUpcoming + stats.moviesArchived + 
                   stats.branches + stats.showtimesTotal + stats.campaigns}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Eye size={16} className="text-blue-400" />
                  <span className="text-gray-300 text-sm">Vizyondaki Filmler</span>
                </div>
                <span className="font-bold text-white">{stats.moviesNowPlaying}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock size={16} className="text-yellow-400" />
                  <span className="text-gray-300 text-sm">Yaklaşan Filmler</span>
                </div>
                <span className="font-bold text-white">{stats.moviesUpcoming}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users size={16} className="text-purple-400" />
                  <span className="text-gray-300 text-sm">Bu Hafta Seans</span>
                </div>
                <span className="font-bold text-white">{stats.showtimesThisWeek}</span>
              </div>

              {stats.unreadMessages > 0 && (
                <Link 
                  to="/admin/contact-messages"
                  className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg hover:bg-orange-500/20 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-orange-400" />
                    <span className="text-orange-300 text-sm font-medium">Okunmamış Mesaj</span>
                  </div>
                  <span className="font-bold text-orange-400">{stats.unreadMessages}</span>
                </Link>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
